/**
 * Jedno źródło wartości systemu wizualnego (redesign, krok B2 zaakceptowany 2026-10-18).
 * Z tego pliku powstaje motyw antd (`appTheme.ts`) i zmienne CSS `--dw-*` (`cssVariables.ts`).
 * Nowy kod nie wpisuje kolorów ani rozmiarów na sztywno: komponenty biorą je z tokenów antd
 * (`theme.useToken()`) albo, w SCSS, ze zmiennych `var(--dw-…)`.
 */
export const dwColor = {
  // Neutralne z nutą granatu marki; kontrast tekstu na białym: 15,7 / 14,4 / 7,9 / 5,4 : 1.
  inkStrong: '#17233b',
  ink: '#1b2a44',
  ink2: '#42526b',
  ink3: '#5b6b84',
  // Tylko stan wyłączony (WCAG 1.4.3 go zwalnia), mimo to 3,5:1 na tle panelu.
  inkDisabled: '#74849b',
  lineStrong: '#b8c3d1',
  line: '#d5dde8',
  lineSoft: '#edf1f6',
  surface: '#ffffff',
  canvas: '#f4f7fa',
  hover: '#f0f4f9',
  selected: '#eaf2fb',
  // Tło zaznaczonej karty wyboru: ledwie widoczny niebieski, ramkę niesie action.
  selectedSoft: '#f7fbfe',
  // Jeden kolor działania (biały tekst 5,4:1), granat tylko dla marki, jasny niebieski tylko UI.
  action: '#1e6eb0',
  actionHover: '#185d96',
  actionActive: '#144f80',
  brand: '#2c4790',
  signal: '#2f90d6',
  okFg: '#0b6b4f',
  okBg: '#e6f6ef',
  okBorder: '#a9dfc6',
  warnFg: '#8c4f00',
  warnBg: '#fff4e0',
  warnBorder: '#f3cf8f',
  errFg: '#b42335',
  errBg: '#fdecee',
  errBorder: '#f4b9c1',
  errSolid: '#c42b3d',
  errSolidHover: '#a52233',
  infoFg: '#1e5f99',
  infoBg: '#e8f2fb',
  infoBorder: '#b7d5ef',
  neutralFg: '#4a5a74',
  neutralBg: '#f4f7fa',
  neutralBorder: '#dbe2ec',
  onColor: '#ffffff',
  // Paleta wykresów raportu okresowego (D2): slot = pozycja grupy wśród AKTYWNYCH grup obszaru (rosnąco po id), od 9. grupy
  // `groupOther`. Elementy wykresu (≥ 3:1 do bieli); tekst nigdy nie stoi na tych kolorach. Te same wartości w PDF.
  group1: '#1f7a72',
  group2: '#b06a12',
  group3: '#5b5fb8',
  group4: '#b5476b',
  group5: '#9c6b4e',
  group6: '#8a4f9e',
  group7: '#559d6d',
  group8: '#6b7d2a',
  groupOther: '#7b8597',
  // Skala sekwencyjna kalendarza obrotu (odcienie action); kroki 0–2 mają < 3:1, więc dzień niesie też podpis.
  seq0: '#edf1f6',
  seq1: '#d6e6f5',
  seq2: '#aacbea',
  seq3: '#75a8d9',
  seq4: '#3f86c5',
  seq5: '#1e6eb0',
  seq6: '#144f80',
  // Ciepły akcent marki wyłącznie dla wstęg na ekranach /auth (decyzja 2026-10-23); nie dla tekstu ani kontrolek.
  authAmber: '#f2a541',
} as const;

/** Kolejność slotów kategorycznych palety grup (pozycja 1–8; dalej `groupOther`). */
export const dwGroupSlots = [
  'group1',
  'group2',
  'group3',
  'group4',
  'group5',
  'group6',
  'group7',
  'group8',
] as const;

export const dwFont = {
  sans: "'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif",
  // Identyfikatory przepisywane znak po znaku (certyfikaty, WZ, numery PoS).
  mono: "'IBM Plex Mono', ui-monospace, Consolas, monospace",
} as const;

/** Role typografii: rozmiar / interlinia / grubość (px). */
export const dwType = {
  pageTitle: { size: 28, line: 36, weight: 600 },
  section: { size: 20, line: 28, weight: 600 },
  subsection: { size: 16, line: 24, weight: 600 },
  body: { size: 14, line: 20, weight: 400 },
  label: { size: 14, line: 20, weight: 500 },
  strong: { size: 14, line: 20, weight: 600 },
  small: { size: 12, line: 16, weight: 400 },
  // Plex Mono: o 1 px mniejszy niż treść, bo przy tym samym rozmiarze optycznie dominuje.
  identifier: { size: 13, line: 20, weight: 400 },
} as const;

export const dwSpace = [4, 8, 12, 16, 20, 24, 32, 40, 48, 64] as const;

export const dwRadius = { badge: 4, control: 6, panel: 8 } as const;

export const dwControlHeight = { sm: 28, md: 32, lg: 40 } as const;

export const dwLayout = {
  sidebar: 240,
  header: 60,
  contentMax: 1280,
  gutter: 40,
  gutterMobile: 16,
  detailPanel: 284,
  formColumn: 640,
  modal: 496,
  /** Okno z formularzem (dodawanie/edycja dostawcy): szersze od okna skutków. */
  modalWide: 560,
} as const;

/** Obwódka fokusu: kolor `signal` z przezroczystością, zawsze 3 px. */
export const dwFocus = { color: 'rgba(47, 144, 214, 0.35)', width: 3 } as const;

export const dwShadow = {
  popover: '0 5px 15px rgba(23, 35, 59, 0.12)',
  modal: '0 5px 15px rgba(23, 35, 59, 0.12), 0 15px 35px rgba(23, 35, 59, 0.08)',
  control: '0 1px 1px rgba(23, 35, 59, 0.08)',
  focus: `0 0 0 ${dwFocus.width}px ${dwFocus.color}`,
} as const;

export const dwOverlay = 'rgba(178, 192, 212, 0.6)';

export const dwMotion = { fast: 120, base: 200, ease: 'cubic-bezier(0.2, 0.8, 0.2, 1)' } as const;
